import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

const GUEST_MODE_KEY = 'wellness_cowgirl_guest_mode';
const GUEST_TOUR_COMPLETED_KEY = 'wellness_cowgirl_guest_tour_completed';

interface PendingAction {
  route: string;
  action?: string;
}

interface GuestContextType {
  isGuest: boolean;
  hasCompletedTour: boolean;
  pendingAction: PendingAction | null;
  enterGuestMode: () => void;
  exitGuestMode: () => void;
  promptSignUp: (message?: string) => void;
  markTourCompleted: () => void;
  setPendingAction: (action: PendingAction | null) => void;
  clearPendingAction: () => void;
}

const GuestContext = createContext<GuestContextType | undefined>(undefined);

export const GuestProvider = ({ children }: { children: ReactNode }) => {
  const [isGuest, setIsGuest] = useState(() => 
    localStorage.getItem(GUEST_MODE_KEY) === 'true'
  );
  const [hasCompletedTour, setHasCompletedTour] = useState(() =>
    localStorage.getItem(GUEST_TOUR_COMPLETED_KEY) === 'true'
  );
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [signUpMessage, setSignUpMessage] = useState('');
  const [pendingAction, setPendingActionState] = useState<PendingAction | null>(null);
  const navigate = useNavigate();

  const enterGuestMode = () => {
    localStorage.setItem(GUEST_MODE_KEY, 'true');
    setIsGuest(true);
    navigate('/');
  };

  const exitGuestMode = () => {
    localStorage.removeItem(GUEST_MODE_KEY);
    localStorage.removeItem(GUEST_TOUR_COMPLETED_KEY);
    setIsGuest(false);
    setHasCompletedTour(false);
    navigate('/auth');
  };

  const promptSignUp = (message?: string) => {
    setSignUpMessage(message || 'Please sign in or sign up to use this feature');
    setShowSignUpPrompt(true);
  };

  const markTourCompleted = () => {
    localStorage.setItem(GUEST_TOUR_COMPLETED_KEY, 'true');
    setHasCompletedTour(true);
  };

  const setPendingAction = (action: PendingAction | null) => {
    setPendingActionState(action);
  };

  const clearPendingAction = () => {
    setPendingActionState(null);
  };

  return (
    <GuestContext.Provider 
      value={{ 
        isGuest, 
        hasCompletedTour,
        pendingAction,
        enterGuestMode, 
        exitGuestMode, 
        promptSignUp,
        markTourCompleted,
        setPendingAction,
        clearPendingAction
      }}
    >
      {children}
    </GuestContext.Provider>
  );
};

export const useGuest = () => {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error('useGuest must be used within GuestProvider');
  }
  return context;
};
